import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import api, { taskApi } from '../lib/api';
import {
  Plus, Search, Clock, ListTodo,
  Loader2, FolderKanban, Users, User, Briefcase,
  Calendar, Flag, Trash2, Edit3, UserCircle, Repeat,
  MoreHorizontal, Copy, FileText, Hash, SlidersHorizontal, X,
  ChevronDown, ChevronUp, Mic, MicOff, LayoutGrid, List,
  AlertTriangle, CheckCircle2, MessageSquare, Eye, EyeOff, Send,
  History, Mail, Globe, UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';
import AppLayout from '../components/AppLayout';
import UnifiedTaskModal from '../components/UnifiedTaskModal';
import AITaskBar, { quickParse, needsLLM } from '../components/AITaskBar';
import SubtaskList from '../components/SubtaskList';
import TaskStatusBadge from '../components/TaskStatusBadge';
import KanbanBoard from '../components/KanbanBoard';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { TASK_STATUSES, OPEN_STATUSES, getStatusConfig, DATE_FILTERS, getDateCategory, getDateCategoryColor, KANBAN_GROUP_OPTIONS } from '../lib/taskStatus';

const AUTO_REFRESH_INTERVAL = 30000;

const InlineTitleEditor = ({ taskId, initialTitle, onSave, onCancel }) => {
  const [value, setValue] = useState(initialTitle);
  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => { if (value.trim() && value.trim() !== initialTitle) onSave(value.trim()); else onCancel(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (value.trim() && value.trim() !== initialTitle) onSave(value.trim()); else onCancel(); } if (e.key === 'Escape') onCancel(); }}
      autoFocus
      className="text-sm font-medium flex-1 min-w-0 h-7 px-1"
      data-testid={`inline-title-input-${taskId}`}
    />
  );
};

const PriorityIcon = ({ priority }) => {
  switch (priority) {
    case 'urgent': return <div className="priority-dot-urgent" />;
    case 'high': return <Flag className="w-3 h-3 text-red-500" />;
    case 'medium': return <Flag className="w-3 h-3 text-amber-500" />;
    case 'low': return <Flag className="w-3 h-3 text-emerald-500" />;
    default: return null;
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'urgent': return 'priority-badge-urgent';
    case 'high': return 'priority-badge-high';
    case 'medium': return 'priority-badge-medium';
    case 'low': return 'priority-badge-low';
    default: return 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300';
  }
};

// Mobile Quick Task Bar
const MobileQuickTask = ({ onTaskCreated }) => {
  const { workspaces } = useWorkspace();
  const { user } = useAuth();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const micReadyRef = useRef(false);
  const mimeTypeRef = useRef('');
  const fileExtRef = useRef('webm');

  // Pre-warm microphone for instant start
  useEffect(() => {
    const prewarm = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 } });
        streamRef.current = stream;
        mimeTypeRef.current = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
                              MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
                              MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
        fileExtRef.current = mimeTypeRef.current.includes('mp4') ? 'mp4' : 'webm';
        micReadyRef.current = true;
      } catch { micReadyRef.current = false; }
    };
    prewarm();
    return () => {
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
      if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); }
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    setLoading(true);
    try {
      let parsed;
      if (needsLLM(input)) {
        const parseRes = await api.post('/ai/parse-task', { text: input });
        parsed = parseRes.data;
      } else {
        parsed = quickParse(input);
      }
      const personalWs = workspaces.find(ws => ws.workspace_type === 'personal');
      const today = new Date().toISOString().split('T')[0];
      await taskApi.create({
        title: parsed.title || input.trim(),
        description: parsed.description,
        due_date: parsed.due_date || today,
        due_time: parsed.due_time,
        priority: parsed.priority || 'medium',
        labels: parsed.labels || [],
        workspace_id: personalWs?.workspace_id || null,
        assigned_to: user?.user_id || null,
        is_recurring: parsed.is_recurring || false,
        recurrence_rule: parsed.recurrence_rule || null,
        status: 'assigned',
      });
      toast.success('Task created!');
      setInput('');
      onTaskCreated();
    } catch {
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const stopRecording = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const createTaskFromVoice = useCallback(async (audioBlob, ext = 'webm') => {
    setVoiceProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, `voice.${ext}`);
      const transcribeRes = await api.post('/voice/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const text = transcribeRes.data?.transcription || transcribeRes.data?.text || transcribeRes.data?.transcript;
      if (!text || !text.trim()) {
        toast.error('No speech detected');
        return;
      }

      const parseRes = await api.post('/ai/parse-task', { text });
      const parsed = parseRes.data;
      const personalWs = workspaces.find(ws => ws.workspace_type === 'personal');
      const today = new Date().toISOString().split('T')[0];
      await taskApi.create({
        title: parsed.title || text.trim(),
        description: parsed.description,
        due_date: parsed.due_date || today,
        due_time: parsed.due_time,
        priority: parsed.priority || 'medium',
        labels: parsed.labels || [],
        workspace_id: personalWs?.workspace_id || null,
        assigned_to: user?.user_id || null,
        is_recurring: parsed.is_recurring || false,
        recurrence_rule: parsed.recurrence_rule || null,
        status: 'assigned',
      });
      toast.success('Task created from voice!');
      onTaskCreated();
    } catch {
      toast.error('Voice task creation failed');
    } finally {
      setVoiceProcessing(false);
    }
  }, [workspaces, user, onTaskCreated]);

  const startRecording = useCallback(async () => {
    try {
      let stream = streamRef.current;
      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 } });
        streamRef.current = stream;
        if (!mimeTypeRef.current) {
          mimeTypeRef.current = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
                                MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
                                MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
          fileExtRef.current = mimeTypeRef.current.includes('mp4') ? 'mp4' : 'webm';
        }
      }

      setIsRecording(true);

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      analyserRef.current = analyser;

      const recorderOptions = mimeTypeRef.current ? { mimeType: mimeTypeRef.current, audioBitsPerSecond: 128000 } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current || 'audio/webm' });
        if (audioBlob.size > 1000) {
          createTaskFromVoice(audioBlob, fileExtRef.current);
        }
      };

      mediaRecorder.start(200);

      let silenceStart = null;
      let speechDetected = false;
      const SILENCE_THRESHOLD = 12;
      const SILENCE_DURATION = 2500;
      const MAX_WAIT_FOR_SPEECH = 8000;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const startTime = Date.now();

      const checkSilence = () => {
        if (!analyserRef.current || !mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

        if (avg >= SILENCE_THRESHOLD) {
          speechDetected = true;
          silenceStart = null;
        } else {
          if (speechDetected) {
            if (!silenceStart) silenceStart = Date.now();
            else if (Date.now() - silenceStart > SILENCE_DURATION) { stopRecording(); return; }
          } else if (Date.now() - startTime > MAX_WAIT_FOR_SPEECH) {
            stopRecording();
            return;
          }
        }
        silenceTimerRef.current = setTimeout(checkSilence, 80);
      };
      setTimeout(checkSilence, 500);
      setTimeout(() => { if (mediaRecorderRef.current?.state === 'recording') stopRecording(); }, 30000);

    } catch {
      toast.error('Microphone access denied');
      setIsRecording(false);
    }
  }, [createTaskFromVoice, stopRecording]);

  const toggleVoice = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-1.5 w-full overflow-hidden">
      <div className="flex-1 min-w-0 relative">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Quick task..." className="h-9 text-base pr-8 bg-white dark:bg-slate-900 w-full" disabled={loading || voiceProcessing} data-testid="mobile-ai-task-input" />
        {(loading || voiceProcessing) && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#4A9CC7]" />}
      </div>
      <Button type="submit" size="sm" disabled={loading || voiceProcessing || !input.trim()} className="h-9 w-9 p-0 flex-shrink-0 bg-[#4A9CC7] hover:bg-[#3d8ab5]" data-testid="mobile-ai-create-btn">
        <Plus className="w-4 h-4" />
      </Button>
      <Button type="button" size="sm" onClick={toggleVoice} disabled={voiceProcessing} className={`h-9 w-9 p-0 flex-shrink-0 ${isRecording ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-purple-600 hover:bg-purple-700'}`} data-testid="mobile-voice-btn">
        {voiceProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </Button>
    </form>
  );
};

// Inline Comment Input
const InlineCommentInput = ({ taskId, onSubmit }) => {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim() || sending) return;
    setSending(true);
    await onSubmit(taskId, value);
    setValue('');
    setSending(false);
  };
  return (
    <form onSubmit={handleSubmit} className="flex-1 flex items-center gap-1.5 min-w-0">
      <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Add comment..." className="h-8 text-base md:text-xs bg-muted/50 dark:bg-slate-900/50 border-transparent focus:border-[#4A9CC7] placeholder:text-muted-foreground/50" data-testid={`inline-comment-${taskId}`} />
      {value.trim() && (
        <Button type="submit" variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#4A9CC7] shrink-0" disabled={sending}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      )}
    </form>
  );
};

// Inline Value Input
const InlineValueInput = ({ taskId, currentValue, onSubmit }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentValue ?? '');
  const handleBlur = () => {
    setEditing(false);
    const numStr = String(value).trim();
    if (numStr !== String(currentValue ?? '')) onSubmit(taskId, numStr);
  };
  if (!editing) {
    return (
      <button onClick={() => { setEditing(true); setValue(currentValue ?? ''); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#4A9CC7] shrink-0 h-7 px-1.5 rounded border border-transparent hover:border-muted-foreground/20" data-testid={`inline-value-btn-${taskId}`}>
        <Hash className="w-3 h-3" />
        <span>{currentValue ?? '-'}</span>
      </button>
    );
  }
  return (
    <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} onBlur={handleBlur} onKeyDown={(e) => { if (e.key === 'Enter') handleBlur(); }} autoFocus className="w-20 h-7 text-xs shrink-0" placeholder="Value" data-testid={`inline-value-input-${taskId}`} />
  );
};

const Tasks = () => {
  const { workspaces } = useWorkspace();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [assignedToFilter, setAssignedToFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(OPEN_STATUSES);
  const [dateFilter, setDateFilter] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [auditTask, setAuditTask] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('tasks_view_mode') || 'list');
  const [kanbanGroupBy, setKanbanGroupBy] = useState(() => localStorage.getItem('tasks_kanban_group') || 'status');
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
    const saved = localStorage.getItem('tasks_filters_collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [showExtras, setShowExtras] = useState(() => {
    const saved = localStorage.getItem('tasks_show_extras');
    return saved ? JSON.parse(saved) : true;
  });
  const [myTasksActive, setMyTasksActive] = useState(false);
  const [mobileView, setMobileView] = useState('tasks');
  const [inlineEdit, setInlineEdit] = useState(null);
  const [editProjectList, setEditProjectList] = useState([]);
  const [editMemberList, setEditMemberList] = useState([]);
  const wsCacheRef = useRef({});
  const [wsMembersMap, setWsMembersMap] = useState({});
  const [showAllOnLogin, setShowAllOnLogin] = useState(true);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const saveTimerRef = useRef(null);
  const abortRef = useRef(null);
  const pendingStatusRef = useRef(new Set()); // Track tasks with in-flight status updates

  const handleMyTasksToggle = () => {
    if (myTasksActive) {
      setAssignedToFilter([]);
      setMyTasksActive(false);
    } else {
      setAssignedToFilter(user?.user_id ? [user.user_id] : []);
      setStatusFilter(OPEN_STATUSES);
      setDateFilter([]);
      setWorkspaceFilter([]);
      setMyTasksActive(true);
    }
  };

  const [expandedComments, setExpandedComments] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [transferCreatorTask, setTransferCreatorTask] = useState(null);

  const handleInlineUpdate = async (taskId, field, value) => {
    try {
      const updateData = { [field]: value || '' };
      if (field === 'workspace_id') updateData.project_id = '';
      await taskApi.update(taskId, updateData);
      setTasks(prev => prev.map(t => {
        if (t.task_id !== taskId) return t;
        const u = { ...t };
        if (field === 'title') u.title = value;
        if (field === 'due_date') u.due_date = value || null;
        if (field === 'assigned_to') {
          u.assigned_to = value || null;
          const wsMembers = wsMembersMap[t.workspace_id] || editMemberList;
          const m = wsMembers.find(x => x.user_id === value) || allMembers.find(x => x.user_id === value);
          u.assigned_to_name = m ? (m.name || m.email) : null;
        }
        if (field === 'workspace_id') {
          u.workspace_id = value;
          const ws = workspaces.find(w => w.workspace_id === value);
          u.workspace_name = ws?.name || '';
          u.project_id = null;
          u.project_name = null;
        }
        if (field === 'project_id') {
          u.project_id = value || null;
          const p = editProjectList.find(x => x.project_id === value);
          u.project_name = p?.name || null;
          u.project_color = p?.color || '#4A9CC7';
        }
        return u;
      }));
      setInlineEdit(null);
      toast.success('Task updated');
    } catch {
      toast.error('Failed to update task');
      setInlineEdit(null);
    }
  };

  const fetchWorkspaceData = async (workspaceId) => {
    if (!workspaceId) { setEditProjectList([]); setEditMemberList([]); return; }
    if (wsCacheRef.current[workspaceId]) {
      setEditProjectList(wsCacheRef.current[workspaceId].projects);
      setEditMemberList(wsCacheRef.current[workspaceId].members);
      return;
    }
    try {
      const res = await api.get(`/workspaces/${workspaceId}`);
      const data = { projects: res.data.projects || [], members: res.data.members || [] };
      wsCacheRef.current[workspaceId] = data;
      setEditProjectList(data.projects);
      setEditMemberList(data.members);
      setWsMembersMap(prev => ({ ...prev, [workspaceId]: data.members }));
    } catch { setEditProjectList([]); setEditMemberList([]); }
  };

  const handleToggleVisibility = async (taskId, currentVis) => {
    const newVis = currentVis === 'workspace' ? 'private' : 'workspace';
    try {
      await api.patch(`/tasks/${taskId}/visibility`, { visibility: newVis });
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, visibility: newVis } : t));
      toast.success(newVis === 'workspace' ? 'Task visible to workspace' : 'Task set to private');
    } catch { toast.error('Failed to update visibility'); }
  };

  const handleTransferCreator = async (taskId, newUserId) => {
    try {
      await api.patch(`/tasks/${taskId}/transfer-creator`, { user_id: newUserId });
      const member = (wsMembersMap[transferCreatorTask?.workspace_id] || allMembers).find(m => m.user_id === newUserId);
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, user_id: newUserId, created_by_name: member?.name || member?.email } : t));
      setTransferCreatorTask(null);
      toast.success('Task creator transferred');
    } catch { toast.error('Failed to transfer creator'); }
  };

  // Pre-fetch workspace members for all visible task workspaces (for subtask assignment)
  useEffect(() => {
    const wsIds = [...new Set(tasks.map(t => t.workspace_id).filter(Boolean))];
    wsIds.forEach(async (wsId) => {
      if (wsCacheRef.current[wsId]) {
        setWsMembersMap(prev => prev[wsId] ? prev : { ...prev, [wsId]: wsCacheRef.current[wsId].members });
        return;
      }
      try {
        const res = await api.get(`/workspaces/${wsId}`);
        const data = { projects: res.data.projects || [], members: res.data.members || [] };
        wsCacheRef.current[wsId] = data;
        setWsMembersMap(prev => ({ ...prev, [wsId]: data.members }));
      } catch {}
    });
  }, [tasks]);

  // Save preferences
  useEffect(() => { localStorage.setItem('tasks_filters_collapsed', JSON.stringify(filtersCollapsed)); }, [filtersCollapsed]);

  // Load filter preferences on mount
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const res = await api.get('/task-filter-preferences');
        const prefs = res.data;
        const shouldApply = !prefs.show_all_on_login && prefs.filters;
        setShowAllOnLogin(prefs.show_all_on_login !== false);
        if (shouldApply) {
          const f = prefs.filters;
          if (f.workspaceFilter?.length) setWorkspaceFilter(f.workspaceFilter);
          if (f.projectFilter?.length) setProjectFilter(f.projectFilter);
          if (f.priorityFilter?.length) setPriorityFilter(f.priorityFilter);
          if (f.assignedToFilter?.length) setAssignedToFilter(f.assignedToFilter);
          if (f.dateFilter?.length) setDateFilter(f.dateFilter);
          if (f.statusFilter?.length) setStatusFilter(f.statusFilter);
        }
      } catch {}
      setPrefsLoaded(true);
    };
    loadPrefs();
  }, []);

  // Auto-save filter preferences (debounced)
  useEffect(() => {
    if (!prefsLoaded) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const filters = { workspaceFilter, projectFilter, priorityFilter, assignedToFilter, dateFilter, statusFilter };
      api.put('/task-filter-preferences', { filters, show_all_on_login: showAllOnLogin }).catch(() => {});
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [workspaceFilter, projectFilter, priorityFilter, assignedToFilter, dateFilter, statusFilter, showAllOnLogin, prefsLoaded]);
  useEffect(() => { localStorage.setItem('tasks_show_extras', JSON.stringify(showExtras)); }, [showExtras]);
  useEffect(() => { localStorage.setItem('tasks_view_mode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('tasks_kanban_group', kanbanGroupBy); }, [kanbanGroupBy]);

  // Check if user is manager for any workspace
  const [userManagerWorkspaces, setUserManagerWorkspaces] = useState([]);
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get('/workspaces');
        const managerWs = (res.data || []).filter(ws =>
          ws.members?.some(m => m.user_id === user?.user_id && ['owner', 'admin'].includes(m.role))
        ).map(ws => ws.workspace_id);
        setUserManagerWorkspaces(managerWs);
      } catch { }
    };
    if (user) fetchRoles();
  }, [user]);

  const isManagerForTask = (task) => {
    if (user?.role === 'admin' || user?.role === 'superadmin') return true;
    return task.workspace_id && userManagerWorkspaces.includes(task.workspace_id);
  };

  const fetchTasks = useCallback(async (silent = false) => {
    // Abort previous in-flight fetch
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!silent) setLoading(true);
    try {
      const params = {};
      if (statusFilter.length > 0 && statusFilter.length < TASK_STATUSES.length) {
        params.status = statusFilter.join(',');
      }
      if (dateFilter.length > 0) {
        params.date_filter = dateFilter.join(',');
        params.client_date = format(new Date(), 'yyyy-MM-dd');
      }
      if (priorityFilter.length > 0) params.priority = priorityFilter.join(',');
      if (workspaceFilter.length > 0) params.workspace_id = workspaceFilter.join(',');
      if (projectFilter.length > 0) params.project_id = projectFilter.join(',');
      if (assignedToFilter.length > 0) params.assigned_to = assignedToFilter.join(',');

      const response = await taskApi.getAll(params);
      // Bail if this fetch was aborted (a newer fetch superseded it)
      if (controller.signal.aborted) return;

      let taskData = response.data || [];

      // Smart sort: When My Tasks is active, personal+today first → overdue → upcoming → rest
      // Otherwise: Overdue → Today → Upcoming → No Date
      const personalWsId = workspaces.find(ws => ws.workspace_type === 'personal')?.workspace_id;
      
      taskData.sort((a, b) => {
        const catOrder = { overdue: 0, today: 1, upcoming: 2, no_date: 3, completed: 4 };
        const catA = getDateCategory(a.due_date, a.status === 'completed');
        const catB = getDateCategory(b.due_date, b.status === 'completed');

        if (myTasksActive && personalWsId) {
          // Personal + today tasks get highest priority
          const aIsPersonalToday = a.workspace_id === personalWsId && catA === 'today';
          const bIsPersonalToday = b.workspace_id === personalWsId && catB === 'today';
          if (aIsPersonalToday && !bIsPersonalToday) return -1;
          if (!aIsPersonalToday && bIsPersonalToday) return 1;
          // Then personal workspace tasks
          const aIsPersonal = a.workspace_id === personalWsId;
          const bIsPersonal = b.workspace_id === personalWsId;
          if (aIsPersonal && !bIsPersonal) return -1;
          if (!aIsPersonal && bIsPersonal) return 1;
        }

        const orderA = catOrder[catA] ?? 3;
        const orderB = catOrder[catB] ?? 3;
        if (orderA !== orderB) return orderA - orderB;
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });

      // Preserve optimistic status updates for tasks with pending API calls
      if (pendingStatusRef.current.size > 0) {
        setTasks(prev => {
          const pendingMap = {};
          prev.forEach(t => { if (pendingStatusRef.current.has(t.task_id)) pendingMap[t.task_id] = t; });
          return taskData.map(t => pendingMap[t.task_id] ? { ...t, status: pendingMap[t.task_id].status, completed: pendingMap[t.task_id].completed } : t);
        });
      } else {
        setTasks(taskData);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      if (!silent) toast.error('Failed to load tasks');
    } finally {
      if (!controller.signal.aborted && !silent) setLoading(false);
    }
  }, [statusFilter, dateFilter, priorityFilter, workspaceFilter, projectFilter, assignedToFilter]);

  // URL params
  useEffect(() => {
    const wsParam = searchParams.get('workspace');
    const projParam = searchParams.get('project');
    const targetWs = workspaces.find(ws => ws.workspace_id === wsParam);
    const isPersonalWs = targetWs?.workspace_type === 'personal';
    const effectiveWsFilter = isPersonalWs ? 'personal' : wsParam;
    if (wsParam && !workspaceFilter.includes(effectiveWsFilter)) {
      setWorkspaceFilter([effectiveWsFilter]);
      if (projParam) sessionStorage.setItem('pending_project_filter', projParam);
    } else if (projParam && projects.some(p => p.project_id === projParam)) {
      setProjectFilter([projParam]);
    }
  }, [searchParams, workspaces]);

  useEffect(() => {
    const fetchWorkspaceData = async () => {
      if (workspaceFilter.length > 0) {
        try {
          const res = await api.get('/workspaces/projects/multi', { params: { workspace_ids: workspaceFilter.join(',') } });
          setProjects(res.data.projects || []);
          setMembers(res.data.members || []);
          const pendingProject = sessionStorage.getItem('pending_project_filter');
          if (pendingProject) {
            if ((res.data.projects || []).some(p => p.project_id === pendingProject)) setProjectFilter([pendingProject]);
            sessionStorage.removeItem('pending_project_filter');
          }
        } catch { setProjects([]); setMembers([]); }
      } else {
        setProjects([]); setMembers([]); setProjectFilter([]);
      }
    };
    fetchWorkspaceData();
  }, [workspaceFilter, workspaces]);

  useEffect(() => {
    const fetchAllMembers = async () => {
      try {
        const response = await api.get('/users/assignable');
        setAllMembers(response.data.users || []);
      } catch { setAllMembers([]); }
    };
    fetchAllMembers();
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Immediately show loading when filters change (prevents flash of old tasks)
  const filterDepsKey = useMemo(() =>
    JSON.stringify([statusFilter, dateFilter, priorityFilter, workspaceFilter, projectFilter, assignedToFilter]),
    [statusFilter, dateFilter, priorityFilter, workspaceFilter, projectFilter, assignedToFilter]
  );
  const prevFilterDepsRef = useRef(filterDepsKey);
  useEffect(() => {
    if (prevFilterDepsRef.current !== filterDepsKey) {
      prevFilterDepsRef.current = filterDepsKey;
      setLoading(true);
    }
  }, [filterDepsKey]);

  // Listen for task-created events from sidebar Add Task / Voice Input
  useEffect(() => {
    const handler = () => fetchTasks();
    window.addEventListener('task-created', handler);
    return () => window.removeEventListener('task-created', handler);
  }, [fetchTasks]);

  useEffect(() => {
    if (inlineEdit) return;
    const intervalId = setInterval(() => fetchTasks(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchTasks, inlineEdit]);

  const handleStatusChange = async (taskId, newStatus) => {
    // Optimistic update — instant UI feedback
    const prevTasks = tasks;
    pendingStatusRef.current.add(taskId);
    setTasks(prev => prev.map(t =>
      t.task_id === taskId ? { ...t, status: newStatus, completed: newStatus === 'completed' } : t
    ));
    const label = getStatusConfig(newStatus).label;
    toast.success(`Status updated to ${label}`);

    try {
      await taskApi.updateStatus(taskId, newStatus);
      pendingStatusRef.current.delete(taskId);
      if (newStatus === 'completed') fetchTasks(true);
    } catch (error) {
      // Revert on failure
      pendingStatusRef.current.delete(taskId);
      setTasks(prevTasks);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleComplete = async (taskId) => {
    const task = tasks.find(t => t.task_id === taskId);
    if (!task) return;

    // If task requires approval and user is not manager, suggest using status dropdown
    if (task.requires_approval && !isManagerForTask(task) && task.status !== 'approved') {
      toast.error('This task requires manager approval. Use the status dropdown to move to "Awaiting Review".');
      return;
    }

    try {
      const res = await taskApi.toggleComplete(taskId);
      if (res.data?.correction_task_created) {
        toast.success('Correction task created for the same date.');
        fetchTasks();
      } else if (task?.is_recurring && !task?.completed) {
        toast.success('Task completed! Next occurrence created.');
        fetchTasks();
      } else {
        fetchTasks();
        toast.success('Task updated!');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && detail.includes('subtask')) {
        toast.error(detail);
      } else {
        toast.error('Failed to update task');
      }
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await taskApi.delete(taskId);
      setTasks(prev => prev.filter(t => t.task_id !== taskId));
      toast.success('Task deleted');
    } catch { toast.error('Failed to delete task'); }
  };

  const handleTaskCreated = () => { setShowModal(false); setEditingTask(null); fetchTasks(); };
  const handleEdit = (task) => { setEditingTask(task); setShowModal(true); };
  const handleDuplicate = async (taskId) => {
    try {
      const response = await api.post(`/tasks/${taskId}/duplicate`);
      toast.success(`Task duplicated: ${response.data.title}`);
      fetchTasks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to duplicate task'); }
  };
  const handleSaveAsTemplate = async (task) => {
    try {
      await api.post('/templates', { task_id: task.task_id, name: task.title, description: task.description });
      toast.success('Template created');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create template'); }
  };

  const handleInlineComment = async (taskId, content) => {
    if (!content.trim()) return;
    try {
      await taskApi.addComment(taskId, content.trim());
      // Refresh to get updated comments
      const res = await taskApi.get(taskId);
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, comments: res.data.comments || [] } : t));
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleInlineValue = async (taskId, value) => {
    const numVal = value === '' ? null : parseFloat(value);
    if (value !== '' && isNaN(numVal)) return;
    try {
      await taskApi.update(taskId, { value: numVal });
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, value: numVal } : t));
    } catch { toast.error('Failed to update value'); }
  };

  const toggleComments = (taskId) => {
    setExpandedComments(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const filteredTasks = tasks.filter(task => {
    if (!searchQuery) return true;
    return task.title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const formatTaskDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return 'Today';
      if (isTomorrow(date)) return 'Tomorrow';
      return format(date, 'MMM d');
    } catch { return dateStr; }
  };

  const getDateColor = (dateStr, completed) => {
    if (!dateStr || completed) return 'text-gray-400';
    try {
      const date = parseISO(dateStr);
      if (isPast(date) && !isToday(date)) return 'text-red-500';
      if (isToday(date)) return 'text-orange-500';
      return 'text-blue-500';
    } catch { return 'text-gray-500'; }
  };

  // Count helpers
  const totalTasks = tasks.length;
  const openCount = tasks.filter(t => t.status !== 'completed').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  const activeFilterCount = [
    workspaceFilter.length > 0, projectFilter.length > 0, priorityFilter.length > 0,
    assignedToFilter.length > 0, dateFilter.length > 0,
    statusFilter.length !== OPEN_STATUSES.length || statusFilter.some(s => !OPEN_STATUSES.includes(s))
  ].filter(Boolean).length;

  const clearFilters = () => {
    setWorkspaceFilter([]); setProjectFilter([]); setPriorityFilter([]);
    setAssignedToFilter([]); setDateFilter([]); setStatusFilter(OPEN_STATUSES);
  };

  const toggleStatusInFilter = (statusValue) => {
    setStatusFilter(prev => {
      if (prev.includes(statusValue)) {
        return prev.filter(s => s !== statusValue);
      }
      return [...prev, statusValue];
    });
  };

  // Quick filter presets
  const setOpenTasks = () => setStatusFilter(OPEN_STATUSES);
  const setAllStatuses = () => setStatusFilter(TASK_STATUSES.map(s => s.value));
  const setCompletedOnly = () => setStatusFilter(['completed']);

  // ============ RENDER ============

  const StatusFilterChips = ({ className = '' }) => (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {TASK_STATUSES.map(s => {
        const active = statusFilter.includes(s.value);
        return (
          <button
            key={s.value}
            onClick={() => toggleStatusInFilter(s.value)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-all ${
              active ? s.color : 'bg-transparent text-muted-foreground border-dashed border-muted-foreground/30 opacity-50 hover:opacity-75'
            }`}
            data-testid={`status-chip-${s.value}`}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${active ? s.dotColor : 'bg-muted-foreground/40'}`} />
            {s.label}
          </button>
        );
      })}
    </div>
  );

  const dateFilterOptions = DATE_FILTERS.filter(f => f.value !== 'all').map(f => ({
    value: f.value,
    label: f.label,
    dot: f.value === 'overdue' ? 'bg-red-500' : f.value === 'today' ? 'bg-orange-500' : f.value === 'upcoming' ? 'bg-blue-500' : 'bg-gray-400'
  }));

  const workspaceOptions = [
    { value: 'personal', label: 'Personal' },
    ...workspaces.filter(ws => ws.workspace_type !== 'personal').map(ws => ({ value: ws.workspace_id, label: ws.name }))
  ];

  const projectOptions = [
    { value: 'none', label: 'No Project' },
    ...projects.map(p => ({ value: p.project_id, label: p.name }))
  ];

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent', dot: 'bg-red-500 animate-pulse' },
    { value: 'high', label: 'High', dot: 'bg-red-500' },
    { value: 'medium', label: 'Medium', dot: 'bg-amber-500' },
    { value: 'low', label: 'Low', dot: 'bg-emerald-500' }
  ];

  const memberOptions = [
    { value: 'unassigned', label: 'Unassigned' },
    ...(workspaceFilter.length > 0 ? members : allMembers).map(m => ({ value: m.user_id, label: m.name || m.email }))
  ];

  const renderTaskCard = (task) => {
    const isCompleted = task.status === 'completed';
    const dateCat = getDateCategory(task.due_date, isCompleted);
    const dateBorderClass = dateCat === 'overdue' ? 'border-l-4 border-l-red-400' :
      dateCat === 'today' ? 'border-l-4 border-l-orange-400' :
      dateCat === 'upcoming' ? 'border-l-4 border-l-blue-400' :
      isCompleted ? 'border-l-4 border-l-gray-300' : '';
    const commentsExpanded = expandedComments[task.task_id];
    const hasComments = task.comments && task.comments.length > 0;

    return (
      <Card key={task.task_id} className={`p-2.5 md:p-3 hover:shadow-sm transition-shadow dark:bg-slate-800 dark:border-slate-700 ${dateBorderClass} ${isCompleted ? 'opacity-60' : ''}`} data-testid={`task-card-${task.task_id}`}>
        <div className="flex items-start gap-2 md:gap-3">
          {/* Status Badge — inline on desktop, skip on mobile (shown below title) */}
          <div className="mt-0.5 shrink-0 hidden md:block">
            <TaskStatusBadge
              status={task.status || 'assigned'}
              requiresApproval={task.requires_approval}
              isManager={isManagerForTask(task)}
              onStatusChange={(newStatus) => handleStatusChange(task.task_id, newStatus)}
              compact={false}
            />
          </div>

          <div className="flex-1 min-w-0">
            {/* Title row */}
            <div className="flex items-start gap-1.5 md:gap-2">
              {inlineEdit?.taskId === task.task_id && inlineEdit?.field === 'title' ? (
                <InlineTitleEditor
                  taskId={task.task_id}
                  initialTitle={task.title}
                  onSave={(newTitle) => handleInlineUpdate(task.task_id, 'title', newTitle)}
                  onCancel={() => setInlineEdit(null)}
                />
              ) : (
                <p
                  className={`text-sm font-medium flex-1 min-w-0 cursor-pointer hover:text-[#4A9CC7] transition-colors ${isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}`}
                  onClick={() => { setInlineEdit({ taskId: task.task_id, field: 'title' }); }}
                  title="Click to edit title"
                  data-testid={`task-title-${task.task_id}`}
                >
                  {task.title}
                </p>
              )}
              {task.is_recurring && (
                <span className="flex items-center gap-1 text-[10px] text-[#4A9CC7] bg-[#4A9CC7]/10 px-1.5 py-0.5 rounded shrink-0" title="Recurring task">
                  <Repeat className="w-3 h-3" />
                  <span className="hidden md:inline">{task.recurrence_rule?.frequency}</span>
                </span>
              )}
              {task.requires_approval && (
                <span className="hidden md:inline text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded shrink-0" title="Requires approval">
                  Approval
                </span>
              )}
              {task.visibility === 'workspace' && (
                <span className="hidden md:inline text-[10px] text-[#4A9CC7] bg-[#4A9CC7]/10 px-1.5 py-0.5 rounded shrink-0 flex items-center gap-0.5" title="Visible to all workspace members" data-testid={`visibility-badge-${task.task_id}`}>
                  <Globe className="w-3 h-3" />Workspace
                </span>
              )}
              {task.email_metadata && (
                <span className="hidden md:inline text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded shrink-0 flex items-center gap-0.5" title="Created via email">
                  <Mail className="w-3 h-3" />Email
                </span>
              )}
            </div>

            {/* Description preview with expand/collapse */}
            {task.description && (
              <div className="mt-1" data-testid={`task-desc-section-${task.task_id}`}>
                {expandedDescriptions[task.task_id] ? (
                  <div className="text-xs text-muted-foreground bg-muted/30 dark:bg-slate-900/40 rounded p-2 whitespace-pre-wrap break-words max-h-60 overflow-y-auto" data-testid={`task-desc-full-${task.task_id}`}>
                    {task.description}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground line-clamp-2 break-words" data-testid={`task-desc-preview-${task.task_id}`}>
                    {task.description}
                  </p>
                )}
                {task.description.split('\n').length > 2 || task.description.length > 120 ? (
                  <button
                    onClick={() => setExpandedDescriptions(prev => ({ ...prev, [task.task_id]: !prev[task.task_id] }))}
                    className="text-[11px] text-[#4A9CC7] hover:underline mt-0.5 flex items-center gap-0.5"
                    data-testid={`toggle-desc-${task.task_id}`}
                  >
                    {expandedDescriptions[task.task_id] ? <><ChevronUp className="w-3 h-3" />Show Less</> : <><ChevronDown className="w-3 h-3" />Show More</>}
                  </button>
                ) : null}
              </div>
            )}

            {/* Mobile: status + meta in a compact row */}
            <div className="flex flex-wrap items-center gap-1.5 mt-1 md:hidden">
              <TaskStatusBadge
                status={task.status || 'assigned'}
                requiresApproval={task.requires_approval}
                isManager={isManagerForTask(task)}
                onStatusChange={(newStatus) => handleStatusChange(task.task_id, newStatus)}
                compact={true}
              />
              {task.due_date && (
                <span className={`text-[11px] flex items-center gap-0.5 ${getDateColor(task.due_date, isCompleted)}`}>
                  <Calendar className="w-3 h-3" />
                  {formatTaskDate(task.due_date)}
                </span>
              )}
              {task.priority && (
                <span className={`text-[11px] flex items-center gap-0.5 ${task.priority === 'high' || task.priority === 'urgent' ? 'text-red-500' : task.priority === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`}>
                  <Flag className="w-3 h-3" />
                </span>
              )}
              {task.workspace_name && (
                <span className="text-[11px] text-muted-foreground truncate max-w-[80px]">{task.workspace_name}</span>
              )}
              {task.assigned_to_name && (
                <span className="text-[11px] text-muted-foreground flex items-center gap-0.5 truncate max-w-[80px]">
                  <UserCircle className="w-3 h-3" />{task.assigned_to_name.split(' ')[0]}
                </span>
              )}
              {hasComments && (
                <button onClick={() => toggleComments(task.task_id)} className={`flex items-center gap-0.5 text-[11px] ${commentsExpanded ? 'text-[#4A9CC7]' : 'text-muted-foreground'}`} data-testid={`mobile-toggle-comments-${task.task_id}`}>
                  <MessageSquare className="w-3 h-3" /> {task.comments.length}
                </button>
              )}
            </div>

            {/* Desktop: full meta row — inline editable */}
            <div className="hidden md:flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs">
              {/* Workspace */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-muted-foreground hover:text-[#4A9CC7] transition-colors" data-testid={`inline-workspace-${task.task_id}`}>
                    <FolderKanban className="w-3 h-3" />
                    {task.workspace_name || <span className="opacity-50">+ Workspace</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 max-h-56 overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
                  {workspaces.filter(ws => ws.workspace_type !== 'personal').map(ws => (
                    <DropdownMenuItem key={ws.workspace_id} onClick={() => handleInlineUpdate(task.task_id, 'workspace_id', ws.workspace_id)} className={`text-xs ${task.workspace_id === ws.workspace_id ? 'bg-[#4A9CC7]/10 font-medium' : ''}`} data-testid={`inline-ws-option-${ws.workspace_id}`}>
                      <FolderKanban className="w-3 h-3 mr-2 shrink-0" />{ws.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Project */}
              <DropdownMenu onOpenChange={(open) => { if (open) fetchWorkspaceData(task.workspace_id); }}>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-muted-foreground hover:text-[#4A9CC7] transition-colors" data-testid={`inline-project-${task.task_id}`}>
                    {task.project_name ? (
                      <><div className="w-2 h-2 rounded-full" style={{ backgroundColor: task.project_color || '#4A9CC7' }} />{task.project_name}</>
                    ) : (
                      <span className="opacity-50">+ Project</span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 max-h-56 overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
                  {task.project_id && (
                    <>
                      <DropdownMenuItem onClick={() => handleInlineUpdate(task.task_id, 'project_id', '')} className="text-xs text-red-500">Clear project</DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {editProjectList.map(p => (
                    <DropdownMenuItem key={p.project_id} onClick={() => handleInlineUpdate(task.task_id, 'project_id', p.project_id)} className={`text-xs ${task.project_id === p.project_id ? 'bg-[#4A9CC7]/10 font-medium' : ''}`} data-testid={`inline-proj-option-${p.project_id}`}>
                      <div className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ backgroundColor: p.color || '#4A9CC7' }} />{p.name}
                    </DropdownMenuItem>
                  ))}
                  {editProjectList.length === 0 && <DropdownMenuItem disabled className="text-xs text-muted-foreground">No projects available</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Date */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className={`flex items-center gap-1 transition-colors hover:text-[#4A9CC7] ${task.due_date ? getDateColor(task.due_date, isCompleted) : 'text-muted-foreground/50'}`} data-testid={`inline-date-${task.task_id}`}>
                    <Calendar className="w-3 h-3" />
                    {task.due_date ? (<>{formatTaskDate(task.due_date)}{task.due_time && ` at ${task.due_time}`}</>) : '+ Date'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="start">
                  <div className="space-y-2">
                    <input type="date" value={task.due_date || ''} onChange={(e) => handleInlineUpdate(task.task_id, 'due_date', e.target.value)} className="text-sm border rounded px-2 py-1.5 w-full dark:bg-slate-800 dark:border-slate-600" data-testid={`inline-date-input-${task.task_id}`} />
                    {task.due_date && (
                      <button onClick={() => handleInlineUpdate(task.task_id, 'due_date', '')} className="text-xs text-red-500 hover:underline flex items-center gap-1">
                        <X className="w-3 h-3" />Clear date
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Assigned member — workspace-scoped */}
              <DropdownMenu onOpenChange={(open) => { if (open) fetchWorkspaceData(task.workspace_id); }}>
                <DropdownMenuTrigger asChild>
                  <button className={`flex items-center gap-1 transition-colors hover:text-[#4A9CC7] ${task.assigned_to_name ? 'text-purple-600 dark:text-purple-400' : 'text-muted-foreground/50'}`} data-testid={`inline-assigned-${task.task_id}`}>
                    <UserCircle className="w-3 h-3" />
                    {task.assigned_to_name || '+ Assign'}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 max-h-56 overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
                  {task.assigned_to && (
                    <>
                      <DropdownMenuItem onClick={() => handleInlineUpdate(task.task_id, 'assigned_to', '')} className="text-xs text-red-500" data-testid={`inline-unassign-${task.task_id}`}>Unassign</DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {editMemberList.map(m => (
                    <DropdownMenuItem key={m.user_id} onClick={() => handleInlineUpdate(task.task_id, 'assigned_to', m.user_id)} className={`text-xs ${task.assigned_to === m.user_id ? 'bg-purple-50 dark:bg-purple-900/30 font-medium' : ''}`} data-testid={`inline-member-option-${m.user_id}`}>
                      <UserCircle className="w-3 h-3 mr-2 shrink-0" />{m.name || m.email}
                    </DropdownMenuItem>
                  ))}
                  {editMemberList.length === 0 && <DropdownMenuItem disabled className="text-xs text-muted-foreground">No workspace members</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>

              {showExtras && task.value !== null && task.value !== undefined && (
                <span className="flex items-center gap-1 text-[#4A9CC7] dark:text-[#5fb3dc] font-medium">
                  <Hash className="w-3 h-3" /> {task.value}
                </span>
              )}
              {hasComments && (
                <button onClick={() => toggleComments(task.task_id)} className="flex items-center gap-1 text-muted-foreground hover:text-[#4A9CC7]" data-testid={`toggle-comments-${task.task_id}`}>
                  <MessageSquare className="w-3 h-3" /> {task.comments.length}
                </button>
              )}
            </div>

            <SubtaskList
              taskId={task.task_id}
              subtasks={task.subtasks || []}
              onUpdate={(updatedSubtasks) => {
                setTasks(prev => prev.map(t => t.task_id === task.task_id ? { ...t, subtasks: updatedSubtasks } : t));
              }}
              collapsibleOnMobile={true}
              members={wsMembersMap[task.workspace_id] || []}
              highlightUserId={assignedToFilter.length === 1 && assignedToFilter[0] !== 'unassigned' ? assignedToFilter[0] : null}
            />

            {/* Inline extras: comments & value */}
            {showExtras && (
              <div className="mt-2 space-y-2">
                {/* Existing comments */}
                {commentsExpanded && hasComments && (
                  <div className="bg-muted/40 dark:bg-slate-900/60 rounded-lg p-2 space-y-1.5 max-h-32 overflow-y-auto" data-testid={`comments-list-${task.task_id}`}>
                    {task.comments.slice(-5).map((c, i) => (
                      <div key={i} className="text-xs">
                        <span className="font-medium text-foreground">{c.user_name || 'User'}:</span>
                        <span className="text-muted-foreground ml-1 break-words whitespace-pre-wrap">{c.content}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Inline comment input + value input */}
                <div className="flex gap-2 items-start">
                  <InlineCommentInput taskId={task.task_id} onSubmit={handleInlineComment} />
                  <InlineValueInput taskId={task.task_id} currentValue={task.value} onSubmit={handleInlineValue} />
                </div>
              </div>
            )}
          </div>

          {task.priority && (
            <Badge className={`text-[10px] flex items-center gap-1 hidden md:flex ${getPriorityColor(task.priority)}`}>
              <PriorityIcon priority={task.priority} />
              <span>{task.priority}</span>
            </Badge>
          )}

          <div className="flex items-center gap-0.5 shrink-0">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-[#4A9CC7]" onClick={() => handleEdit(task)}>
              <Edit3 className="w-3.5 h-3.5" />
            </Button>

            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-[#4A9CC7]">
                <MoreHorizontal className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-slate-800 dark:border-slate-700">
              <DropdownMenuItem onClick={() => setAuditTask(task)} data-testid={`audit-trail-${task.task_id}`}><History className="w-4 h-4 mr-2" />Audit Trail</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDuplicate(task.task_id)}><Copy className="w-4 h-4 mr-2" />Duplicate</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSaveAsTemplate(task)}><FileText className="w-4 h-4 mr-2" />Save as Template</DropdownMenuItem>
              {task.workspace_id && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleToggleVisibility(task.task_id, task.visibility)} data-testid={`toggle-visibility-${task.task_id}`}>
                    <Globe className="w-4 h-4 mr-2" />{task.visibility === 'workspace' ? 'Make Private' : 'Visible to Workspace'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { fetchWorkspaceData(task.workspace_id); setTransferCreatorTask(task); }} data-testid={`transfer-creator-${task.task_id}`}>
                    <UserCog className="w-4 h-4 mr-2" />Change Creator
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleDelete(task.task_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <AppLayout>
      <div className="h-full flex flex-col bg-background" data-testid="tasks-page">
        {/* AI Task Bar - Desktop */}
        <div className="hidden md:block flex-shrink-0 px-6 pt-4">
          <AITaskBar onTaskCreated={() => fetchTasks()} />
        </div>

        {/* ===== MOBILE HEADER ===== */}
        <div className="md:hidden flex-shrink-0 px-3 py-2 border-b border-border bg-card">
          {/* Top row: title + action buttons */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-base font-bold text-foreground">Tasks</h1>
              <p className="text-[11px] text-muted-foreground">{openCount} open, {completedCount} done</p>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant={mobileSearchOpen ? "default" : "outline"} size="sm" className={`h-8 w-8 p-0 ${mobileSearchOpen ? 'bg-[#4A9CC7]' : ''}`} onClick={() => setMobileSearchOpen(!mobileSearchOpen)} data-testid="mobile-search-toggle">
                <Search className={`w-3.5 h-3.5 ${mobileSearchOpen ? 'text-white' : ''}`} />
              </Button>
              <Button variant={myTasksActive ? "default" : "outline"} size="sm" className={`h-8 px-2.5 text-xs ${myTasksActive ? 'bg-[#4A9CC7] text-white' : ''}`} onClick={handleMyTasksToggle} data-testid="mobile-my-tasks-btn">
                <User className="w-3.5 h-3.5 mr-1" /> My Tasks
              </Button>
              <Button variant="outline" size="sm" className={`h-8 w-8 p-0 ${showExtras ? 'text-[#4A9CC7]' : ''}`} onClick={() => setShowExtras(!showExtras)} data-testid="mobile-toggle-extras">
                {showExtras ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </Button>
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 relative">
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    {activeFilterCount > 0 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#4A9CC7] text-white text-[9px] rounded-full flex items-center justify-center">{activeFilterCount}</span>}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[80vh] rounded-t-2xl">
                  <SheetHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <SheetTitle>Filters</SheetTitle>
                      {activeFilterCount > 0 && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#4A9CC7]">Clear all</Button>}
                    </div>
                  </SheetHeader>
                  <div className="space-y-4 overflow-y-auto max-h-[60vh]">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Status</label>
                      <StatusFilterChips />
                      <div className="flex gap-2 mt-1">
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={setOpenTasks}>Open</Button>
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={setAllStatuses}>All</Button>
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={setCompletedOnly}>Completed</Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date</label>
                      <MultiSelectFilter options={dateFilterOptions} selected={dateFilter} onChange={setDateFilter} placeholder="All Dates" icon={Calendar} className="w-full h-9" data-testid="mobile-date-filter" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Workspace</label>
                      <MultiSelectFilter options={workspaceOptions} selected={workspaceFilter} onChange={setWorkspaceFilter} placeholder="All Workspaces" icon={FolderKanban} className="w-full h-9" data-testid="mobile-workspace-filter" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Project</label>
                      <MultiSelectFilter options={projectOptions} selected={projectFilter} onChange={setProjectFilter} placeholder="All Projects" icon={Briefcase} className="w-full h-9" data-testid="mobile-project-filter" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Priority</label>
                      <MultiSelectFilter options={priorityOptions} selected={priorityFilter} onChange={setPriorityFilter} placeholder="All Priorities" icon={Flag} className="w-full h-9" data-testid="mobile-priority-filter" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Assigned To</label>
                      <MultiSelectFilter options={memberOptions} selected={assignedToFilter} onChange={setAssignedToFilter} placeholder="All Members" icon={UserCircle} className="w-full h-9" searchable data-testid="mobile-member-filter" />
                    </div>
                    <div className="space-y-2 pt-2 border-t">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!showAllOnLogin} onChange={(e) => setShowAllOnLogin(!e.target.checked)} className="rounded border-gray-300" />
                        <span className="font-medium">Remember filters on next login</span>
                      </label>
                      <p className="text-xs text-muted-foreground">When enabled, your current filter selection will be applied automatically next time you open Tasks.</p>
                    </div>
                  </div>
                  <div className="pt-4 mt-4 border-t">
                    <Button className="w-full bg-[#4A9CC7] hover:bg-[#3d8ab5]" onClick={() => setMobileFiltersOpen(false)}>Apply Filters</Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Mobile Quick Task - compact */}
          <div className="mb-2 bg-gradient-to-r from-[#4A9CC7]/5 to-blue-50 dark:from-[#4A9CC7]/10 dark:to-slate-800 rounded-lg border border-[#4A9CC7]/20 p-2 overflow-hidden">
            <MobileQuickTask onTaskCreated={fetchTasks} />
          </div>

          {/* Mobile View Toggle: Tasks vs Board */}
          <div className="flex items-center bg-muted rounded-lg p-0.5 border border-border mb-2">
            <Button variant={mobileView === 'tasks' ? 'default' : 'ghost'} size="sm" className={`flex-1 h-7 text-xs ${mobileView === 'tasks' ? 'bg-[#4A9CC7] text-white hover:bg-[#3d8ab5] shadow-sm' : 'text-foreground'}`} onClick={() => setMobileView('tasks')} data-testid="mobile-tasks-view-btn">
              <List className="w-3.5 h-3.5 mr-1" /> Tasks
            </Button>
            <Button variant={mobileView === 'board' ? 'default' : 'ghost'} size="sm" className={`flex-1 h-7 text-xs ${mobileView === 'board' ? 'bg-[#4A9CC7] text-white hover:bg-[#3d8ab5] shadow-sm' : 'text-foreground'}`} onClick={() => setMobileView('board')} data-testid="mobile-board-view-btn">
              <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Board
            </Button>
          </div>

          {/* Mobile Board Group-by selector */}
          {mobileView === 'board' && (
            <div className="flex items-center gap-1.5 mb-2" data-testid="mobile-board-groupby">
              {KANBAN_GROUP_OPTIONS.map(opt => (
                <Button key={opt.value} variant={kanbanGroupBy === opt.value ? 'default' : 'outline'} size="sm"
                  className={`h-7 text-[11px] px-2.5 ${kanbanGroupBy === opt.value ? 'bg-[#4A9CC7] text-white hover:bg-[#3d8ab5]' : 'text-muted-foreground'}`}
                  onClick={() => setKanbanGroupBy(opt.value)} data-testid={`mobile-group-${opt.value}`}>
                  {opt.value === 'status' ? 'Status' : opt.value === 'assignee' ? 'Member' : 'Date'}
                </Button>
              ))}
            </div>
          )}

          {mobileView === 'tasks' && mobileSearchOpen && (
            <div className="relative mb-2 animate-in slide-in-from-top-2 duration-200">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 pr-8 h-9 text-base" autoFocus data-testid="mobile-search-input" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><X className="w-4 h-4" /></button>}
            </div>
          )}

          {/* Active Filter Pills - compact */}
          {mobileView === 'tasks' && activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {dateFilter.length > 0 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 flex items-center gap-0.5 h-5">
                  <Calendar className="w-2.5 h-2.5" />{dateFilter.length === 1 ? DATE_FILTERS.find(f => f.value === dateFilter[0])?.label : `${dateFilter.length} dates`}
                  <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setDateFilter([])} />
                </Badge>
              )}
              {workspaceFilter.length > 0 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 flex items-center gap-0.5 h-5">
                  <FolderKanban className="w-2.5 h-2.5" />{workspaceFilter.length === 1 ? workspaceOptions.find(o => o.value === workspaceFilter[0])?.label : `${workspaceFilter.length} workspaces`}
                  <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setWorkspaceFilter([])} />
                </Badge>
              )}
              {projectFilter.length > 0 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 flex items-center gap-0.5 h-5">
                  <Briefcase className="w-2.5 h-2.5" />{projectFilter.length === 1 ? projectOptions.find(o => o.value === projectFilter[0])?.label : `${projectFilter.length} projects`}
                  <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setProjectFilter([])} />
                </Badge>
              )}
              {priorityFilter.length > 0 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 flex items-center gap-0.5 h-5">
                  <Flag className="w-2.5 h-2.5" />{priorityFilter.length === 1 ? priorityFilter[0] : `${priorityFilter.length} priorities`}
                  <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setPriorityFilter([])} />
                </Badge>
              )}
              {assignedToFilter.length > 0 && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 flex items-center gap-0.5 h-5">
                  <UserCircle className="w-2.5 h-2.5" />{assignedToFilter.length === 1 ? memberOptions.find(o => o.value === assignedToFilter[0])?.label : `${assignedToFilter.length} members`}
                  <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setAssignedToFilter([])} />
                </Badge>
              )}
            </div>
          )}
          {mobileView === 'tasks' && myTasksActive && (
            <div className="flex flex-wrap gap-1 mt-1">
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 flex items-center gap-0.5 h-5 bg-[#4A9CC7]/10 text-[#4A9CC7]">
                <User className="w-2.5 h-2.5" />My Tasks
                <X className="w-2.5 h-2.5 cursor-pointer" onClick={handleMyTasksToggle} />
              </Badge>
            </div>
          )}
        </div>

        {/* ===== DESKTOP HEADER ===== */}
        <div className="hidden md:block flex-shrink-0 border-b border-border bg-card px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-foreground">Tasks</h1>
              <p className="text-sm text-muted-foreground">{openCount} open &middot; {completedCount} completed</p>
            </div>
            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center bg-muted rounded-lg p-0.5 border border-border">
                <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className={`h-7 px-2.5 ${viewMode === 'list' ? 'bg-[#4A9CC7] text-white hover:bg-[#3d8ab5] shadow-sm' : 'text-foreground hover:bg-muted-foreground/10'}`} onClick={() => setViewMode('list')} data-testid="list-view-btn">
                  <List className="w-4 h-4 mr-1" /> List
                </Button>
                <Button variant={viewMode === 'kanban' ? 'default' : 'ghost'} size="sm" className={`h-7 px-2.5 ${viewMode === 'kanban' ? 'bg-[#4A9CC7] text-white hover:bg-[#3d8ab5] shadow-sm' : 'text-foreground hover:bg-muted-foreground/10'}`} onClick={() => setViewMode('kanban')} data-testid="kanban-view-btn">
                  <LayoutGrid className="w-4 h-4 mr-1" /> Board
                </Button>
              </div>
              {viewMode === 'kanban' && (
                <Select value={kanbanGroupBy} onValueChange={setKanbanGroupBy}>
                  <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="kanban-group-select">
                    <SelectValue placeholder="Group by" />
                  </SelectTrigger>
                  <SelectContent>
                    {KANBAN_GROUP_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button onClick={() => setShowModal(true)} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">
                <Plus className="w-4 h-4 mr-2" /> New Task
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setFiltersCollapsed(!filtersCollapsed)} className="text-muted-foreground hover:text-foreground -ml-2" data-testid="toggle-filters-btn">
                  {filtersCollapsed ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronUp className="w-4 h-4 mr-1" />}
                  {filtersCollapsed ? 'Show Filters' : 'Hide Filters'}
                  {activeFilterCount > 0 && <Badge variant="secondary" className="ml-2 text-xs">{activeFilterCount}</Badge>}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowExtras(!showExtras)} className={`text-xs h-7 ${showExtras ? 'text-[#4A9CC7]' : 'text-muted-foreground'}`} data-testid="toggle-extras-btn">
                  {showExtras ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                  {showExtras ? 'Hide Comments' : 'Show Comments'}
                </Button>
              </div>
              {!filtersCollapsed && (
                <div className="flex items-center gap-2">
                  {activeFilterCount > 0 && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#4A9CC7] text-xs">Clear all</Button>}
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleMyTasksToggle} data-testid="my-tasks-btn">
                    <User className="w-3 h-3 mr-1" /> My Tasks
                  </Button>
                </div>
              )}
            </div>

            {!filtersCollapsed && (
              <div className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                {/* Status Chips */}
                <div className="flex items-center gap-2">
                  <StatusFilterChips />
                  <div className="flex gap-1 ml-2 border-l pl-2">
                    <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={setOpenTasks}>Open</Button>
                    <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={setAllStatuses}>All</Button>
                  </div>
                </div>

                {/* Standard Filters Row */}
                <div className="flex flex-wrap gap-2 items-center">
                  <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-9 dark:bg-slate-900 dark:border-slate-700" />
                  </div>
                  <MultiSelectFilter options={dateFilterOptions} selected={dateFilter} onChange={setDateFilter} placeholder="All Dates" icon={Calendar} className="w-[155px] h-9" data-testid="date-filter" />
                  <MultiSelectFilter options={workspaceOptions} selected={workspaceFilter} onChange={setWorkspaceFilter} placeholder="All Workspaces" icon={FolderKanban} className="w-[165px] h-9" data-testid="workspace-filter" />
                  <MultiSelectFilter options={projectOptions} selected={projectFilter} onChange={setProjectFilter} placeholder="All Projects" icon={Briefcase} className="w-[155px] h-9" data-testid="project-filter" />
                  <MultiSelectFilter options={priorityOptions} selected={priorityFilter} onChange={setPriorityFilter} placeholder="Priority" icon={Flag} className="w-[135px] h-9" data-testid="priority-filter" />
                  <MultiSelectFilter options={memberOptions} selected={assignedToFilter} onChange={setAssignedToFilter} placeholder="Assigned To" icon={UserCircle} className="w-[155px] h-9" searchable data-testid="assigned-filter" />
                </div>
                {/* Save preferences toggle */}
                <div className="flex items-center gap-3 pt-1">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="remember-filters-toggle">
                    <input type="checkbox" checked={!showAllOnLogin} onChange={(e) => setShowAllOnLogin(!e.target.checked)} className="rounded border-gray-300 w-3.5 h-3.5" />
                    Remember filters on next login
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ===== TASK CONTENT ===== */}
        <div className="flex-1 overflow-auto px-3 py-2 md:p-6 pb-20 md:pb-6">
          {/* Mobile Board View */}
          {mobileView === 'board' && (
            <div className="md:hidden overflow-x-auto">
              <KanbanBoard
                tasks={filteredTasks}
                groupBy={kanbanGroupBy}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                onStatusChange={handleStatusChange}
                isManager={user?.role === 'admin' || user?.role === 'superadmin'}
                allMembers={allMembers}
              />
            </div>
          )}

          {/* Task List / Desktop view (shown when mobileView=tasks or on desktop) */}
          <div className={mobileView === 'board' ? 'hidden md:block' : ''}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-12">
              <ListTodo className="w-12 h-12 md:w-16 md:h-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4 text-sm md:text-base">{searchQuery ? 'No tasks match your search' : 'No tasks found'}</p>
              <Button onClick={() => setShowModal(true)} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"><Plus className="w-4 h-4 mr-2" /> Create Task</Button>
            </div>
          ) : viewMode === 'kanban' ? (
            <KanbanBoard
              tasks={filteredTasks}
              groupBy={kanbanGroupBy}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onStatusChange={handleStatusChange}
              isManager={user?.role === 'admin' || user?.role === 'superadmin'}
              allMembers={allMembers}
            />
          ) : (
            <div className="space-y-1.5 md:space-y-2">
              {filteredTasks.map(task => renderTaskCard(task))}
            </div>
          )}
          </div>
        </div>

        {/* Mobile FAB */}
        <div className="md:hidden fixed bottom-6 right-6 z-50">
          <Button onClick={() => setShowModal(true)} className="w-14 h-14 rounded-full bg-[#4A9CC7] hover:bg-[#3d8ab5] shadow-lg" data-testid="mobile-add-task-fab">
            <Plus className="w-6 h-6" />
          </Button>
        </div>

        <UnifiedTaskModal open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) setEditingTask(null); }} onTaskCreated={handleTaskCreated} editTask={editingTask} />

        {/* Audit Trail Dialog */}
        <Dialog open={!!auditTask} onOpenChange={(open) => { if (!open) setAuditTask(null); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto dark:bg-slate-900 dark:border-slate-700">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-foreground">
                <History className="w-5 h-5 text-[#4A9CC7]" />
                Audit Trail
              </DialogTitle>
              {auditTask && <p className="text-sm text-muted-foreground truncate mt-1">{auditTask.title}</p>}
            </DialogHeader>
            {auditTask && (
              <div className="space-y-4 mt-2" data-testid="audit-trail-content">
                {/* Created / Modified Summary */}
                <div className="bg-muted/40 rounded-lg p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">Created by</span>
                    <span className="text-foreground font-medium">{auditTask.created_by_name || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">Created on</span>
                    <span className="text-foreground">{auditTask.created_at ? new Date(auditTask.created_at).toLocaleString() : 'N/A'}</span>
                  </div>
                  {auditTask.last_modified_by_name && (
                    <>
                      <div className="border-t border-border my-1" />
                      <div className="flex justify-between items-start">
                        <span className="text-muted-foreground">Last modified by</span>
                        <span className="text-foreground font-medium">{auditTask.last_modified_by_name}</span>
                      </div>
                      <div className="flex justify-between items-start">
                        <span className="text-muted-foreground">Last modified on</span>
                        <span className="text-foreground">{auditTask.updated_at ? new Date(auditTask.updated_at).toLocaleString() : 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Activity Log Timeline */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Activity Log</p>
                  {(!auditTask.activity_log || auditTask.activity_log.length === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No activity recorded yet</p>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
                      <div className="space-y-3">
                        {[...(auditTask.activity_log || [])].reverse().map((entry, idx) => {
                          const actionColors = {
                            created: 'bg-emerald-600',
                            created_via_email: 'bg-amber-500',
                            email_reply_received: 'bg-amber-400',
                            status_changed: 'bg-blue-500',
                            assigned_to_changed: 'bg-purple-500',
                            subtask_added: 'bg-green-500',
                            subtask_deleted: 'bg-red-500',
                            subtask_completed: 'bg-green-500',
                            subtask_reopened: 'bg-orange-500',
                            subtask_reassigned: 'bg-purple-500',
                            priority_changed: 'bg-yellow-500',
                            due_date_changed: 'bg-cyan-500',
                            title_changed: 'bg-gray-500',
                            subtask_title_changed: 'bg-gray-500',
                            workspace_changed: 'bg-indigo-500',
                            project_changed: 'bg-teal-500',
                            visibility_changed: 'bg-sky-500',
                            creator_transferred: 'bg-rose-500'
                          };
                          const dotColor = actionColors[entry.action] || 'bg-gray-400';
                          const isEmailAction = entry.source === 'email';
                          return (
                            <div key={idx} className="flex gap-3 pl-0" data-testid={`audit-entry-${idx}`}>
                              <div className={`w-[15px] h-[15px] rounded-full ${dotColor} flex-shrink-0 mt-0.5 relative z-10 border-2 border-background`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm text-foreground">{entry.description || entry.details || entry.action}</p>
                                  {isEmailAction && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-full" data-testid={`email-badge-${idx}`}>
                                      <Mail className="w-2.5 h-2.5" />via Email
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                                  <span>{entry.user_name || 'System'}</span>
                                  <span>·</span>
                                  <span>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Transfer Creator Dialog */}
        <Dialog open={!!transferCreatorTask} onOpenChange={() => setTransferCreatorTask(null)}>
          <DialogContent className="max-w-sm dark:bg-slate-800 dark:border-slate-700">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 text-[#4A9CC7]" />Change Task Creator</DialogTitle>
              <DialogDescription>
                Transfer creator of "<span className="font-medium">{transferCreatorTask?.title}</span>" to another workspace member.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {editMemberList.map(m => (
                <Button
                  key={m.user_id}
                  variant={transferCreatorTask?.user_id === m.user_id ? 'default' : 'outline'}
                  className="w-full justify-start gap-2 text-sm"
                  onClick={() => handleTransferCreator(transferCreatorTask.task_id, m.user_id)}
                  disabled={transferCreatorTask?.user_id === m.user_id}
                  data-testid={`transfer-to-${m.user_id}`}
                >
                  <UserCircle className="w-4 h-4 shrink-0" />
                  {m.name || m.email}
                  {transferCreatorTask?.user_id === m.user_id && <span className="text-xs text-muted-foreground ml-auto">(current)</span>}
                </Button>
              ))}
              {editMemberList.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No workspace members found</p>}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default Tasks;
